import React from "react";
import { useSelector } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Badge } from "reactstrap";
import { getTranslation, indexLanguages } from "./language";
import moment from "moment";

const iconMap = {
  SOURCE_API_INTEGRATION: ["ri-global-line", "Integración API"],
  SOURCE_USER_ADMIN: ["ri-user-line", "Admin"],
  SOURCE_USER_PUBLISHER: ["ri-user-line", "Agente"],
  SOURCE_SYSTEM: ["ri-settings-2-line", "Sistema"],
  SOURCE_CHATBOT: ["ri-message-3-line", "Chatbot"],
  SOURCE_API: ["ri-global-line", "API"],
  SOURCE_USER_MANAGER: ["ri-team-line", "Manager"],
  SOURCE_TEAM: ["ri-team-line", "Equipo"],
};

const fmt = (d) => (d ? moment(d).format("DD/MM/YYYY HH:mm:ss") : "-");

const ModalAssignHistory = ({ show, toggle, contactSelected: contact, membersOfGroup }) => {
  const language = useSelector((s) => s.Group.language);
  const user = useSelector((s) => s.Auth.user);
  const teams = useSelector((s) => s.Teams.teams) || [];
  const t = (k) => getTranslation(indexLanguages, language, k);

  const agents = (membersOfGroup || []).filter((m) => m.role === "ROLE_PUBLISHER");
  const findAgentEmail = (id) => agents.find((a) => a.tagId === id)?.email;
  const findTeamName = (id) => teams.find((t) => t.id === id)?.name;
  const findUserEmail = (id) => {
    if (!id) return null;
    // Try to find user in membersOfGroup first
    const member = (membersOfGroup || []).find((m) => m.id === id || m._id === id);
    if (member) return member.email;
    // Check if it's the current user
    if (id === user._id || id === user.id) return user.email;
    return null;
  };

  const RoleBadge = ({ roleKey, userId }) => {
    const [icon, label] = iconMap[roleKey] || [];
    const userEmail = findUserEmail(userId);

    return (
      <div>
        <Badge color="light" className="font-size-12 px-2 py-1 d-inline-flex align-items-center">
          {icon && <i className={`${icon} align-bottom mr-1`} />}
          {label || "Desconocido"}
        </Badge>
        {userEmail && (
          <>
            <br />
            <div title={userEmail} className="text-muted font-size-13 ml-1 ellipsis cursor-pointer">
              {userEmail}
            </div>
          </>
        )}
      </div>
    );
  };

  // Process all entries from userTagIds array - they can be agent, team, or both
  const allEntries = (contact?.userTagIds || []).map(entry => {
    const hasUserTagId = entry.userTagId;
    const hasTeamTagId = entry.teamTagId;
    
    if (hasUserTagId && hasTeamTagId) {
      // Agent assignment within a team
      const agentEmail = findAgentEmail(entry.userTagId);
      const teamName = findTeamName(entry.teamTagId);
      return {
        ...entry,
        type: 'agent-team',
        assignedToId: entry.userTagId,
        assignedToName: agentEmail,
        teamId: entry.teamTagId,
        teamName: teamName,
        assignedAt: entry.assignedAt,
        unassignedAt: entry.unassignedAt,
        assignedBy: entry.assignedBy,
        assignedByUserId: entry.assignedByUserId,
        unassignedBy: entry.unassignedBy,
        unassignedByUserId: entry.unassignedByUserId
      };
    } else if (hasTeamTagId && !hasUserTagId) {
      // Team-only assignment
      const teamName = findTeamName(entry.teamTagId);
      return {
        ...entry,
        type: 'team',
        assignedToId: entry.teamTagId,
        assignedToName: teamName,
        assignedAt: entry.assignedTeamAt,
        unassignedAt: entry.unassignedTeamAt,
        assignedBy: entry.assignedTeamBy,
        assignedByUserId: entry.assignedTeamByUserId,
        unassignedBy: entry.unassignedTeamBy,
        unassignedByUserId: entry.unassignedTeamByUserId
      };
    } else {
      // Agent-only assignment
      return {
        ...entry,
        type: 'agent',
        assignedToId: entry.userTagId,
        assignedToName: findAgentEmail(entry.userTagId),
        assignedAt: entry.assignedAt,
        unassignedAt: entry.unassignedAt,
        assignedBy: entry.assignedBy,
        assignedByUserId: entry.assignedByUserId,
        unassignedBy: entry.unassignedBy,
        unassignedByUserId: entry.unassignedByUserId
      };
    }
  });

  // Sort all entries by assignment date
  const entries = allEntries.sort((a, b) => 
    new Date(a.assignedAt) - new Date(b.assignedAt)
  );

  return (
    <Modal isOpen={show} toggle={toggle} centered size="xl">
      <ModalHeader toggle={toggle}>{t("AssignmentHistory")}</ModalHeader>
      <ModalBody className="px-4 pt-4">
        {entries.length === 0 ? (
          <div className="text-center py-4">{t("NoAssignmentHistory")}</div>
        ) : (
          <>
            <div className="assign-table">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th className="align-middle text-left">{t("AssignedTo")}</th>
                    <th className="align-middle text-left">{t("AssignedBy")}</th>
                    <th className="align-middle text-left">{t("AssignmentDate")}</th>
                    <th className="align-middle text-left">{t("UnassignedBy")}</th>
                    <th className="align-middle text-left">{t("UnassignmentDate")}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((i, idx) => {
                    const isActive = i.type === 'agent' 
                      ? contact.userTagId === i.assignedToId && idx === entries.length - 1
                      : i.type === 'team'
                      ? contact.teamTagId === i.assignedToId && idx === entries.length - 1
                      : i.type === 'agent-team'
                      ? (contact.userTagId === i.assignedToId || contact.teamTagId === i.teamId) && idx === entries.length - 1
                      : false;
                    
                    const getBadgeColor = () => {
                      switch(i.type) {
                        case 'agent': return 'primary';
                        case 'team': return 'info';
                        case 'agent-team': return 'success';
                        default: return 'secondary';
                      }
                    };
                    
                    const getBadgeText = () => {
                      switch(i.type) {
                        case 'agent': return t("AgentAssignment");
                        case 'team': return t("TeamAssignment");
                        case 'agent-team': return t("AgentTeamAssignment");
                        default: return "Unknown";
                      }
                    };
                    
                    const getAssignedToText = () => {
                      if (i.type === 'agent-team') {
                        return (
                          <div>
                            <div className="font-weight-semibold">{i.assignedToName || `${t("WithID")} ${i.assignedToId}`}</div>
                            <div className="text-muted font-size-12">en {i.teamName || `${t("WithID")} ${i.teamId}`}</div>
                          </div>
                        );
                      } else {
                        return i.assignedToName ? (
                          <span className="ellipsis">{i.assignedToName}</span>
                        ) : (
                          <>
                            {t("WithID")}
                            <Badge color="light" className="font-size-12 px-2 py-1 ml-1">
                              {i.assignedToId}
                            </Badge>
                          </>
                        );
                      }
                    };
                    
                    return (
                      <tr key={idx} className="align-middle">
                        <td className="align-middle text-left">
                          <div className="d-flex align-items-center">
                            <Badge 
                              color={getBadgeColor()} 
                              className="font-size-11 px-2 py-1 mr-2"
                            >
                              {getBadgeText()}
                            </Badge>
                            {getAssignedToText()}
                          </div>
                        </td>
                        <td className="align-middle text-left">
                          <RoleBadge roleKey={i.assignedBy} userId={i.assignedByUserId} />
                        </td>
                        <td className="align-middle text-left">{fmt(i.assignedAt)}</td>
                        <td className="align-middle text-left">
                          {isActive ? (
                            "-"
                          ) : (
                            <RoleBadge roleKey={i.unassignedBy} userId={i.unassignedByUserId} />
                          )}
                        </td>
                        <td className="align-middle text-left">
                          {isActive ? "Activo" : i.unassignedAt ? fmt(i.unassignedAt) : "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="assign-list">
              {entries.map((i, idx) => {
                const isActive = i.type === 'agent' 
                  ? contact.userTagId === i.assignedToId && idx === entries.length - 1
                  : i.type === 'team'
                  ? contact.teamTagId === i.assignedToId && idx === entries.length - 1
                  : i.type === 'agent-team'
                  ? (contact.userTagId === i.assignedToId || contact.teamTagId === i.teamId) && idx === entries.length - 1
                  : false;
                
                const getBadgeColor = () => {
                  switch(i.type) {
                    case 'agent': return 'primary';
                    case 'team': return 'info';
                    case 'agent-team': return 'success';
                    default: return 'secondary';
                  }
                };
                
                const getBadgeText = () => {
                  switch(i.type) {
                    case 'agent': return t("AgentAssignment");
                    case 'team': return t("TeamAssignment");
                    case 'agent-team': return t("AgentTeamAssignment");
                    default: return "Unknown";
                  }
                };
                
                const getAssignedToText = () => {
                  if (i.type === 'agent-team') {
                    return (
                      <div>
                        <div className="font-weight-semibold">{i.assignedToName || `${t("WithID")} ${i.assignedToId}`}</div>
                        <div className="text-muted font-size-12">en {i.teamName || `${t("WithID")} ${i.teamId}`}</div>
                      </div>
                    );
                  } else {
                    return i.assignedToName ? (
                      <div className="ellipsis">{i.assignedToName}</div>
                    ) : (
                      <>
                        {t("WithID")}
                        <Badge color="light" className="font-size-12 px-2 py-1 ml-1">
                          {i.assignedToId}
                        </Badge>
                      </>
                    );
                  }
                };
                
                return (
                  <div key={idx} className="assign-card">
                    <div className="mb-2">
                      <div className="font-weight-semibold mb-1">{t("AssignedTo")}:</div>
                      <div className="d-flex align-items-center">
                        <Badge 
                          color={getBadgeColor()} 
                          className="font-size-11 px-2 py-1 mr-2"
                        >
                          {getBadgeText()}
                        </Badge>
                        {getAssignedToText()}
                      </div>
                    </div>
                    <div className="mb-2">
                      <div className="font-weight-semibold mb-1">{t("AssignedBy")}:</div>
                      <div>
                        <RoleBadge roleKey={i.assignedBy} userId={i.assignedByUserId} />
                      </div>
                    </div>
                    <div className="mb-2">
                      <div className="font-weight-semibold mb-1">{t("AssignmentDate")}:</div>
                      <div>{fmt(i.assignedAt)}</div>
                    </div>
                    <div className="mb-2">
                      <div className="font-weight-semibold mb-1">{t("UnassignedBy")}:</div>
                      <div>
                        {isActive ? (
                          "-"
                        ) : (
                          <RoleBadge roleKey={i.unassignedBy} userId={i.unassignedByUserId} />
                        )}
                      </div>
                    </div>
                    <div className="mb-1">
                      <div className="font-weight-semibold mb-1">{t("UnassignmentDate")}:</div>
                      <div>{isActive ? "Activo" : i.unassignedAt ? fmt(i.unassignedAt) : "-"}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          {t("Close")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ModalAssignHistory;
